import React, { Component } from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import api from 'api';
import { isMobileAppRuntime } from 'utils';

import { Portal } from 'components/common';
import { GameModal } from 'components/flink-play';
import { playApplause } from 'actions/audioActions';
import { setFamilyPoints, updateLearner } from 'actions/flinkPlayActions';
import classes from './SetGoal.module.scss';
import './CalendarStyles.scss';

const PENDING_MOBILE_GOAL_COMPLETION_KEY = 'flc.mobile.pendingGoalCompletion.v1';

const readPendingMobileGoalCompletion = () => {
  try {
    return JSON.parse(window.localStorage.getItem(PENDING_MOBILE_GOAL_COMPLETION_KEY) || 'null');
  } catch (_) {
    return null;
  }
};

const writePendingMobileGoalCompletion = (value) => {
  try {
    if (value) window.localStorage.setItem(PENDING_MOBILE_GOAL_COMPLETION_KEY, JSON.stringify(value));
    else window.localStorage.removeItem(PENDING_MOBILE_GOAL_COMPLETION_KEY);
  } catch (_) {}
};

const mobileGoalIdentity = (goal = {}) => [
  goal.solution,
  goal.level,
  goal.subject,
  goal.expiredAt ? new Date(goal.expiredAt).toISOString() : '',
].map(value => String(value || '')).join(':');


export class SetGoal extends Component {
  state = {
    selectedLevel: '',
    selectedSubject: '',
    selectedDate: '',
    approvedBy: '',
    modalOpened: false,
  };

  componentDidMount() {
    this.init();
    if (isMobileAppRuntime()) {
      window.addEventListener('online', this.flushPendingMobileGoalCompletion);
      if (typeof navigator === 'undefined' || navigator.onLine !== false) this.flushPendingMobileGoalCompletion();
    }
  }

  componentWillUnmount() {
    if (isMobileAppRuntime()) window.removeEventListener('online', this.flushPendingMobileGoalCompletion);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.learner.reports !== this.props.learner.reports) {
      this.checkIfGoalIsAccomplished();
    }
  }

  init = async () => {
    const {
      familyMembers = [],
      team = [],
      learner: { familyId },
      menuGroups,
    } = this.props;

    const isSingleMember = familyMembers.length === 1;

    const familyMembersInTeam = team.filter(
      (member) => member.familyId === familyId
    );

    const levels = _.chain(menuGroups)
      .map((g) => g.menuLevel)
      .uniq()
      .sort((a, b) => b - a)
      .reverse()
      .value();

    this.setState(
      {
        isSingleMember,
        familyMembersInTeam,
        levels,
        selectedLevel: levels[0],
      },
      () => {
        this.checkIfGoalIsAccomplished();
        this.onLevelChanged();
      }
    );
  };

  onLevelChanged = () => {
    const { menuGroups, reports, translate } = this.props;
    const { selectedLevel, selectedSubject: currentSubject } = this.state;

    if (!selectedLevel) {
      return;
    }

    const subjects = _.chain(menuGroups)
      .filter((g) => g.menuLevel === selectedLevel)
      .map((g) => {
        const subj = reports[g.firstCategory];

        // subj.name is translate string number
        return { label: translate(subj && subj.name), value: g.firstCategory };
      })
      .uniqBy('value')
      .sortBy('label')
      // .reverse()
      .value();

    let selectedSubject = subjects[0] ? subjects[0].value : '';

    if (currentSubject) {
      const hasInLevel = !!_.find(subjects, { value: currentSubject });
      hasInLevel && (selectedSubject = currentSubject);
    }

    this.setState({ subjects, selectedSubject });
  };

  getCurrentGoal = () => {
    const {
      learner: { learningGoals },
      solutionId,
    } = this.props;

    return _.find(learningGoals, { solution: solutionId });
  };

  getGoalGroupIds = (goal) => {
    if (!goal) return [];
    const { menuGroups } = this.props;
    return menuGroups
      .filter(
        (group) =>
          group.menuLevel === goal.level &&
          group.firstCategory === goal.subject
      )
      .map((group) => group.groupId)
      .filter(Boolean);
  };

  checkIfGoalIsAccomplished = () => {
    const {
      learner: { reports },
      translate,
      playApplause,
    } = this.props;

    let currentGoal = this.getCurrentGoal();

    if (!currentGoal) return;

    // Check if expired
    const isExpired = Date.now() > new Date(currentGoal.expiredAt);

    if (isExpired) {
      return this.deleteGoal();
    }

    // Check if accomplished
    // 1. Get all menu groups of current level
    const groupsAtLevel = this.getGoalGroupIds(currentGoal);

    // 2. Check if all mastered
    const allMastered = groupsAtLevel.length > 0 && groupsAtLevel.every(
      (groupId) => reports && reports[groupId] && reports[groupId].mastered
    );

    if (!allMastered) return;

    // Mobile is local-first: celebrate immediately offline, queue the idempotent
    // server award, and use the server-returned awarded value whenever online.
    if (isMobileAppRuntime()) {
      return this.handleMobileGoalAccomplished(currentGoal, groupsAtLevel);
    }

    window.Modals.showInfo({
      message: translate(679, 'You have accompished your learning goal'),
      title: translate(225, 'Congratulations'),
      okHandler: this.completeGoal,
    });

    playApplause();
  };

  showMobileGoalCongratulations = (awarded = 0, withApplause = true) => {
    const { translate, playApplause } = this.props;
    const points = Math.max(0, Math.floor(Number(awarded || 0)));
    const baseMessage = translate(679, 'You have accompished your learning goal');
    let message = baseMessage;

    if (points > 0) {
      const template = translate(3853, '{{message}} +{{points}} Family Points', true);
      message = String(template || '{{message}} +{{points}} Family Points')
        .replace(/\{\{\s*message\s*\}\}/gi, String(baseMessage || ''))
        .replace(/\{\{\s*points\s*\}\}/gi, String(points));
    }

    window.Modals.showInfo({
      message,
      title: translate(225, 'Congratulations'),
    });
    if (withApplause) playApplause();
  };

  completeMobileGoal = async (goal, groupIds, { showCongratulations = false, withApplause = true } = {}) => {
    if (!goal || this.mobileGoalCompletionInFlight) return null;
    this.mobileGoalCompletionInFlight = true;
    try {
      const { learner, updateLearner, setFamilyPoints } = this.props;
      const result = await api.mobileRuntime.completeFamilyGoal({
        solution: goal.solution,
        level: goal.level,
        subject: goal.subject,
        expiredAt: goal.expiredAt,
        groupIds,
      });
      await updateLearner(learner._id, {
        learningGoals: result?.learningGoals || [],
      });
      if (result?.points) setFamilyPoints(result.points);
      writePendingMobileGoalCompletion(null);
      if (showCongratulations) this.showMobileGoalCongratulations(result?.awarded || 0, withApplause);
      return result;
    } finally {
      this.mobileGoalCompletionInFlight = false;
    }
  };

  handleMobileGoalAccomplished = async (goal, groupIds) => {
    const identity = mobileGoalIdentity(goal);
    if (!identity || this.mobileGoalCelebrationIdentity === identity || this.mobileGoalCompletionInFlight) return;

    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      const pending = readPendingMobileGoalCompletion();
      const alreadyCelebrated = pending?.identity === identity && pending?.celebrated === true;
      writePendingMobileGoalCompletion({ identity, goal, groupIds, celebrated: true, familyMemberId: String(this.props.learner?._id || '') });
      this.mobileGoalCelebrationIdentity = identity;
      if (!alreadyCelebrated) this.showMobileGoalCongratulations(0, true);
      return;
    }

    this.mobileGoalCelebrationIdentity = identity;
    try {
      await this.completeMobileGoal(goal, groupIds, { showCongratulations: true, withApplause: true });
    } catch (error) {
      const networkFailure = !error?.response;
      if (networkFailure) {
        writePendingMobileGoalCompletion({ identity, goal, groupIds, celebrated: true, familyMemberId: String(this.props.learner?._id || '') });
        this.mobileGoalCelebrationIdentity = identity;
        this.showMobileGoalCongratulations(0, true);
        return;
      }
      this.mobileGoalCelebrationIdentity = '';
      window.Modals.showError(
        error?.response?.data?.message || error?.message || 'Family Points could not be awarded. Please try again.'
      );
    }
  };

  flushPendingMobileGoalCompletion = async () => {
    if (!isMobileAppRuntime() || this.mobileGoalCompletionInFlight) return;
    const pending = readPendingMobileGoalCompletion();
    if (!pending?.goal || !Array.isArray(pending.groupIds) || !pending.groupIds.length) return;
    if (pending.familyMemberId && String(pending.familyMemberId) !== String(this.props.learner?._id || '')) return;

    try {
      const result = await this.completeMobileGoal(pending.goal, pending.groupIds);
      const awarded = Math.max(0, Math.floor(Number(result?.awarded || 0)));
      if (awarded > 0) this.showMobileGoalCongratulations(awarded, false);
    } catch (_) {
      // Keep the pending completion for the next online event / mount.
    }
  };

  openModal = () => {
    const { translate } = this.props;
    const { isSingleMember, familyMembersInTeam } = this.state;

    // If family member is single in family, then he/she needs to
    // login with someone else from his/her family
    if (!isSingleMember && !familyMembersInTeam.length) {
      return window.Modals.showError(
        translate(
          501,
          'You must log in with another family member to set your learning goal'
        )
      );
    }

    this.setState({ modalOpened: true });
  };

  closeModal = () => this.setState({ modalOpened: false });

  setGoal = async () => {
    const { solutionId, updateLearner, learner } = this.props;
    const {
      selectedLevel,
      selectedSubject,
      selectedDate,
      approvedBy,
    } = this.state;

    const goal = {
      solution: solutionId,
      level: selectedLevel,
      subject: selectedSubject,
      expiredAt: selectedDate,
      approvedBy: approvedBy || undefined,
    };

    if (isMobileAppRuntime()) {
      try {
        const result = await api.mobileRuntime.saveFamilyGoal(goal);
        await updateLearner(learner._id, {
          learningGoals: result?.learningGoals || [],
        });
        this.closeModal();
      } catch (error) {
        window.Modals.showError(
          error?.response?.data?.message || error?.message || 'Learning goal could not be saved.'
        );
      }
      return;
    }

    const result = await updateLearner(learner._id, {
      learningGoals: [...learner.learningGoals, goal],
    });

    if (result.success) {
      this.closeModal();
    }
  };

  completeGoal = async () => {
    const currentGoal = this.getCurrentGoal();
    if (isMobileAppRuntime() && currentGoal) {
      const groupIds = this.getGoalGroupIds(currentGoal);
      try {
        return await this.completeMobileGoal(currentGoal, groupIds);
      } catch (error) {
        return window.Modals.showError(
          error?.response?.data?.message || error?.message || 'Family Points could not be awarded. Please try again.'
        );
      }
    }
    return this.deleteGoal();
  };

  deleteGoal = async () => {
    const { learner, solutionId, updateLearner } = this.props;

    if (isMobileAppRuntime()) {
      try {
        const result = await api.mobileRuntime.deleteFamilyGoal(solutionId);
        await updateLearner(learner._id, {
          learningGoals: result?.learningGoals || [],
        });
      } catch (error) {
        window.Modals.showError(
          error?.response?.data?.message || error?.message || 'Learning goal could not be deleted.'
        );
      }
      return;
    }

    const filteredGoals = learner.learningGoals.filter(
      (g) => g.solution !== solutionId
    );

    await updateLearner(learner._id, {
      learningGoals: filteredGoals,
    });
  };

  deleteGoalButtonHandler = async () => {
    const { translate } = this.props;

    const isConfirmed = await window.Modals.showConfirm(
      translate(680, 'You sure you want to delete your learning goal?')
    );

    if (isConfirmed) this.deleteGoal();
  };

  selectLevel = (selectedLevel) => {
    this.setState({ selectedLevel }, this.onLevelChanged);
  };

  selectSubject = (selectedSubject) => {
    this.setState({ selectedSubject });
  };

  selectDate = (selectedDate) => {
    // Set end of day
    selectedDate.setHours(23, 59, 59, 999);

    this.setState({ selectedDate });
  };

  selectApprovedBy = (approvedBy) => {
    this.setState({ approvedBy });
  };

  render() {
    const { translate } = this.props;
    const {
      modalOpened,
      selectedLevel,
      selectedSubject,
      selectedDate,
      subjects,
      approvedBy,
      isSingleMember,
      familyMembersInTeam,
      levels,
    } = this.state;

    let formattedDate;

    const currentGoal = this.getCurrentGoal();

    if (currentGoal) {
      const date = new Date(currentGoal.expiredAt);
      formattedDate = `${date.toLocaleString('en-US', {
        month: 'long',
      })} ${date.getDate()}, ${date.getFullYear()}`;
    }

    return (
      <>
        <div className={classes.buttonWrapper}>
          {currentGoal ? (
            <>
              <button
                className={classes.button}
                onClick={this.deleteGoalButtonHandler}
              >
                {translate(500, 'Delete Goal')}
              </button>
              <span className={classes.date}>{formattedDate}</span>
            </>
          ) : (
            <button className={classes.button} onClick={this.openModal}>
              {translate(467, 'Set Goal')}
            </button>
          )}
        </div>

        <Portal>
          {modalOpened && (
            <GameModal
              title={translate(448, 'Set Learning Goal')}
              buttons={[
                {
                  title: translate(52, 'Cancel'),
                  clickHandler: this.closeModal,
                },
                {
                  title: translate(467, 'Set Goal'),
                  clickHandler: this.setGoal,
                  disabled:
                    selectedLevel === '' ||
                    selectedSubject === '' ||
                    !selectedDate ||
                    (!isSingleMember && !approvedBy),
                },
              ]}
            >
              <div className={classes.modalContent}>
                <div className={classes.fieldsWrapper}>
                  <div>
                    <label className={classes.label}>
                      {translate(452, 'Select a Level')}
                    </label>

                    <select
                      className={classes.levelSelect}
                      onChange={(e) => this.selectLevel(e.target.value)}
                      value={selectedLevel}
                      size={12}
                    >
                      {levels &&
                        levels.map((l) => (
                          <option key={l} value={l}>
                            {l}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div>
                    <label className={classes.label}>
                      {translate(732, 'Select a Subject')}
                    </label>

                    <select
                      className={classes.subjectSelect}
                      onChange={(e) => this.selectSubject(e.target.value)}
                      value={selectedSubject}
                      size={12}
                    >
                      {subjects &&
                        subjects.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div className={classes.calendarBlock}>
                    <label className={classes.label}>
                      {translate(459, 'Select a Date')}
                    </label>

                    <DatePicker
                      inline
                      calendarClassName="set-goal-calendar"
                      minDate={new Date()}
                      onChange={this.selectDate}
                      selected={selectedDate}
                    />
                  </div>
                </div>

                {!isSingleMember && (
                  <div className={classes.approveWrapper}>
                    <label className={classes.label}>
                      {translate(449, 'Approve By Family Member')}
                    </label>

                    <div className={classes.radioGroup}>
                      {familyMembersInTeam.map((member) => (
                        <label key={member._id}>
                          <input
                            type="radio"
                            checked={member._id === approvedBy}
                            onChange={() => this.selectApprovedBy(member._id)}
                          />
                          <span>{`${member.firstname} ${member.lastname}`}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </GameModal>
          )}
        </Portal>
      </>
    );
  }
}

SetGoal.propTypes = {
  updateLearner: PropTypes.func.isRequired,
  translate: PropTypes.func.isRequired,
  playApplause: PropTypes.func.isRequired,
  setFamilyPoints: PropTypes.func.isRequired,
  familyMembers: PropTypes.array,
  team: PropTypes.array,
  learner: PropTypes.object.isRequired,
  reports: PropTypes.object.isRequired,
  menuGroups: PropTypes.array.isRequired,
  solutionId: PropTypes.string.isRequired,
};

const mapStateToProps = ({ flinkPlay }) => ({
  menuGroups: flinkPlay.solutionMenu.menuGroups,
  translate: flinkPlay.solutionTranslate,
  familyMembers: flinkPlay.familyMembers,
  team: flinkPlay.team,
  learner: flinkPlay.learner,
  reports: flinkPlay.solutionMenu.reports,
  solutionId: flinkPlay.solution._id,
});

export default connect(mapStateToProps, { updateLearner, playApplause, setFamilyPoints })(
  SetGoal
);
